/**
 * Gerenciar BUs — the business units every other module draws its tenants from.
 *
 * Cards, three per row, like Páginas, Tours and Usuários. The x/4 setup indicator rides on each
 * card because an unfinished BU is what this screen exists to surface.
 *
 * Exclusion is soft: the BU leaves the Ativas tab and shows up under Excluídas, from where it can
 * be reactivated. Nothing is ever removed from the database.
 *
 * @module Pages/BUs/BUManager
 */

import { css } from '@emotion/css';
import { UTButton, UTLoading } from '@group-link-one/gl-ui/utilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import {
  createTenant,
  deleteTenant,
  getTenants,
  restoreTenant,
  updateTenant,
} from '../../Services';
import { BUCard } from '../BUCard/BUCard';
import type { BUFormValues } from '../BUFormModal/BUFormModal';
import { BUFormModal } from '../BUFormModal/BUFormModal';
import { BUVersionsModal } from '../BUVersionsModal/BUVersionsModal';
import { DeleteBUModal } from '../DeleteBUModal/DeleteBUModal';
import { RestoreBUModal } from '../RestoreBUModal/RestoreBUModal';
import type { Tenant } from './BUPlatformList.types';
import { useActionToast } from '@/Hooks/useActionToast';
import { usePrivileges } from '@/Hooks/usePrivileges/usePrivilleges';
import { compareTenantSlugs } from '@/Shared/brand';
import { EmptyState } from '@/Shared/components/EmptyState/EmptyState';
import { Tabs } from '@/Shared/components/Tabs/Tabs';
import { MainContent } from '@/Templates/Main/MainContent/MainContent';

const screenStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-base);
`;

const toolbarStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-base);
  flex-wrap: wrap;
`;

const descriptionStyle = css`
  margin: 0;
  max-width: 620px;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const spacerStyle = css`
  flex: 1;
`;

const gridStyle = css`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-base);

  @media (max-width: 1280px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 860px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

type TabId = 'active' | 'deleted';

const BUPlatformList = (): JSX.Element => {
  const queryClient = useQueryClient();
  const { pages: privileges } = usePrivileges();
  const toast = useActionToast();

  const [tab, setTab] = useState<TabId>('active');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<Tenant | undefined>();
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Tenant | undefined>();
  const [restoring, setRestoring] = useState<Tenant | undefined>();
  const [versionsFor, setVersionsFor] = useState<Tenant | undefined>();

  const { data: tenants, isLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => getTenants({ includeInactive: true }),
  });

  const { active, deleted } = useMemo(() => {
    const sorted = [...(tenants ?? [])].sort((a, b) => compareTenantSlugs(a.slug, b.slug));

    return {
      active: sorted.filter(tenant => tenant.isActive),
      deleted: sorted.filter(tenant => !tenant.isActive),
    };
  }, [tenants]);

  const rows = tab === 'active' ? active : deleted;

  const closeForm = () => {
    setIsFormOpen(false);
    setEditing(undefined);
    setFormError(null);
  };

  const saveTenant = useMutation({
    mutationFn: async (values: BUFormValues) => {
      const signature = values.signatureEnabled
        ? { subject: values.signatureSubject.trim() }
        : undefined;

      if (editing) {
        await updateTenant(editing.slug, {
          name: values.name.trim(),
          orgBackendUrl: values.orgBackendUrl.trim() || undefined,
          iconName: values.iconName,
          flagsmithEnvironmentKey: values.flagsmithEnvironmentKey.trim() || null,
          brand: values.brand,
          // null clears the config, which is what turns step 2 back off.
          signatureSubject: signature ? signature.subject : null,
        });
        return;
      }

      await createTenant({
        slug: values.slug.trim(),
        name: values.name.trim(),
        version: values.version.trim(),
        orgBackendUrl: values.orgBackendUrl.trim() || undefined,
        iconName: values.iconName,
        flagsmithEnvironmentKey: values.flagsmithEnvironmentKey.trim() || null,
        brand: values.brand,
        ...(signature ? { signatureConfig: signature } : {}),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success(editing ? 'Business unit salva' : 'Business unit criada');
      closeForm();
    },
    onError: (error: Error) => {
      // Twice on purpose: the toast is what the person notices, the inline message is what stays
      // on screen while they fix the form.
      setFormError(error.message || 'Não foi possível salvar a business unit.');
      toast.failure('Não foi possível salvar a business unit', error);
    },
  });

  const removeTenant = useMutation({
    mutationFn: (tenant: Tenant) => deleteTenant(tenant.slug),
    onSuccess: (_result, tenant) => {
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success(
        `${tenant.slug} foi excluída`,
        'Ela está na aba Excluídas e pode ser reativada.',
      );
      setDeleting(undefined);
      // The BU is not gone, it moved — send the user to where it now lives.
      setTab('deleted');
    },
    onError: (error: Error) => {
      toast.failure('Não foi possível excluir a business unit', error);
    },
  });

  const reactivateTenant = useMutation({
    mutationFn: (tenant: Tenant) => restoreTenant(tenant.slug),
    onSuccess: (_result, tenant) => {
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success(`${tenant.slug} foi reativada`);
      setRestoring(undefined);
      setTab('active');
    },
    onError: (error: Error) => {
      toast.failure('Não foi possível reativar a business unit', error);
    },
  });

  if (isLoading) {
    return (
      <MainContent id='bus-main' styling={{ noBackgroundColor: true, noBorder: true }}>
        <UTLoading type='dots' />
      </MainContent>
    );
  }

  return (
    <MainContent id='bus-main' styling={{ noBackgroundColor: true, noBorder: true }}>
      <div className={screenStyle}>
        <div className={toolbarStyle}>
          <p className={descriptionStyle}>
            Business units e as plataformas vinculadas a cada uma. É daqui que saem os tenants
            disponíveis nos outros módulos.
          </p>
          <div className={spacerStyle} />
          {privileges.BUSINESS_UNITS.CREATE ? (
            <UTButton
              fitContent
              id='bus-create'
              text='Criar BU'
              icon='Arrows.Plus'
              onClick={() => {
                setEditing(undefined);
                setFormError(null);
                setIsFormOpen(true);
              }}
            />
          ) : null}
        </div>

        <Tabs
          activeId={tab}
          items={[
            { id: 'active', label: 'Ativas', badge: active.length },
            { id: 'deleted', label: 'Excluídas', badge: deleted.length },
          ]}
          onChange={id => {
            setTab(id as TabId);
          }}
        />

        {rows.length === 0 ? (
          <EmptyState
            id='bus-empty'
            icon='Misc.Buildings'
            title={
              tab === 'active'
                ? 'Nenhuma business unit cadastrada'
                : 'Nenhuma business unit excluída'
            }
            description={
              tab === 'active'
                ? 'Crie a primeira BU para que os outros módulos tenham tenants para trabalhar.'
                : 'BUs excluídas aparecem aqui e podem ser reativadas a qualquer momento.'
            }
            action={
              tab === 'active' && privileges.BUSINESS_UNITS.CREATE ? (
                <UTButton
                  fitContent
                  text='Criar BU'
                  icon='Arrows.Plus'
                  onClick={() => {
                    setIsFormOpen(true);
                  }}
                />
              ) : undefined
            }
          />
        ) : (
          <div className={gridStyle}>
            {rows.map(tenant => (
              <BUCard
                key={tenant.slug}
                tenant={tenant}
                canEdit={privileges.BUSINESS_UNITS.CREATE}
                canDelete={privileges.BUSINESS_UNITS.DELETE}
                onEdit={target => {
                  setEditing(target);
                  setFormError(null);
                  setIsFormOpen(true);
                }}
                onVersions={setVersionsFor}
                onDelete={setDeleting}
                onRestore={setRestoring}
              />
            ))}
          </div>
        )}
      </div>

      {isFormOpen ? (
        <BUFormModal
          open
          tenant={editing}
          isSaving={saveTenant.isPending}
          error={formError}
          onClose={closeForm}
          onSubmit={values => {
            saveTenant.mutate(values);
          }}
        />
      ) : null}

      {versionsFor ? (
        <BUVersionsModal
          open
          tenant={versionsFor}
          onClose={() => {
            setVersionsFor(undefined);
          }}
        />
      ) : null}

      {deleting ? (
        <DeleteBUModal
          open
          tenant={deleting}
          isDeleting={removeTenant.isPending}
          error={removeTenant.error?.message ?? null}
          onClose={() => {
            removeTenant.reset();
            setDeleting(undefined);
          }}
          onConfirm={target => {
            removeTenant.mutate(target);
          }}
        />
      ) : null}

      {restoring ? (
        <RestoreBUModal
          open
          tenant={restoring}
          isRestoring={reactivateTenant.isPending}
          error={reactivateTenant.error?.message ?? null}
          onClose={() => {
            reactivateTenant.reset();
            setRestoring(undefined);
          }}
          onConfirm={target => {
            reactivateTenant.mutate(target);
          }}
        />
      ) : null}
    </MainContent>
  );
};

export { BUPlatformList };
